'use client'

import { useState } from 'react'
import { TalkingSpeedTest } from '@/components/tools/talking-speed-test'
import { countWords } from '@/components/tools/word-counter'
import { InlineScriptCheck } from '@/components/seo/InlineScriptCheck'
import { Button } from '@/components/ui/button'
import { trackCalcCheckClick } from '@/lib/analytics'

// Owns the pasted text so the pace measurement and the free check below it run
// on the same script: a visitor who pasted a draft to time it should not have
// to paste it again to have it checked. page.tsx is a server component, so the
// shared text lives here; its FAQ and CTAs stay in page.tsx.
const PAGE = 'tool-talking-speed-test'
/** Matches the InlineScriptCheck `page` prop, so the click and the box line up. */
const CHECK_ID = 'talking-speed-test-check'
// Below this the check has nothing to say about hook or structure, so the
// button stays hidden rather than inviting a submission the API would refuse.
const MIN_CHECK_WORDS = 150

export function TalkingSpeedTestContent() {
  const [text, setText] = useState('')
  const words = countWords(text)

  const handleCheckClick = () => {
    trackCalcCheckClick({ page: PAGE, word_count: words })
    document.getElementById(CHECK_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <>
      <TalkingSpeedTest script={text} onScriptChange={setText} />

      {words >= MIN_CHECK_WORDS && (
        <div className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-5 text-center">
          <Button
            type="button"
            size="lg"
            onClick={handleCheckClick}
            className="h-12 w-full px-6 text-[15px] font-medium sm:h-11"
          >
            Check this script (free)
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">
            Hook, structure, and pacing scores plus the single biggest issue quoted from your own
            lines. No login.
          </p>
        </div>
      )}

      {/* After the speed test the visitor has a script open: the free check runs
          here instead of a banner that links to it. The paste box is already
          filled from the paste-and-time mode above; the click does not submit. */}
      <div className="mt-6">
        <InlineScriptCheck
          id={CHECK_ID}
          initialScript={text}
          page={PAGE}
          heading="Pace is only half of it. Check the script, free"
          subheading="Paste the script you just read aloud. You get hook, structure and pacing scores and the one issue costing it the most, quoted from your own lines."
        />
      </div>
    </>
  )
}
